import clsx from 'clsx';

import { EditablePictureEditorContainer } from '~components/generic/editable-picture/EditablePictureEditorContainer';
import { Picture } from '~components/generic/picture/Picture';
import { IEntityComponentProps } from '~interfaces/props';
import { CustomMoodboardPicture } from '~modules/moodboard/CustomMoodboard/CustomMoodboardPicture';
import { getEntityFieldDefinition } from '~services/entity-definitions';

export type EditablePictureProps =
  | IEntityComponentProps & {
      entityType: string;
      proportions: string;
      showWhenEmpty?: boolean;
      showOriginal?: boolean;
      border?: boolean;
      borderRadius?: number;
      onChangePosition: (position: { x: number; y: number }) => void;
      imagePosition: string;
    };

export const EditablePicture = ({
  entityType,
  editing,
  data,
  fieldName,
  proportions,
  origData,
  className,
  children,
  showWhenEmpty,
  placeholder,
  showOriginal,
  onUpload,
  onChangeImage,
  border = false,
  borderRadius = 0,
  customMoodboard,
  onChangePosition,
  imagePosition,
}: EditablePictureProps) => {
  if (!data && !editing && !showWhenEmpty && !placeholder) {
    return null;
  }

  const fieldDef = getEntityFieldDefinition(entityType, fieldName);
  const imgRatioClass = showOriginal && data ? false : true;

  const classes = clsx({
    'c-module__field--editing': editing,
    relative: true,
    [`u-image-ratio--${proportions?.replace(':', '-')}`]: imgRatioClass,
    'u-object-fit-cover-children': true,
    'u-object-fit-cover-children--limit-vertical': true,
    'u-overflow-hidden': true,
    'c-module__content': showOriginal,
    'editable-picture--container': true,
    border: border,
    'rounded-none': borderRadius === 0,
    rounded: borderRadius === 1,
    'rounded-sm': borderRadius === 2,
    'rounded-md': borderRadius === 3,
    'rounded-lg': borderRadius === 4,
    ...className,
  });

  return (
    <div className={!customMoodboard ? classes : ''}>
      {!customMoodboard ? (
        <Picture
          data={data}
          proportions={proportions}
          origData={origData}
          placeholder={placeholder}
          lazyLoad={!editing}
          showOriginal={showOriginal}
        />
      ) : (
        <CustomMoodboardPicture
          borderRadius={borderRadius}
          onChangePosition={onChangePosition}
          data={data}
          proportions={proportions}
          origData={origData}
          placeholder={placeholder}
          lazyLoad={!editing}
          showOriginal={showOriginal}
          editing={editing}
          imagePosition={imagePosition}
        />
      )}
      {children}
      {editing && (
        <EditablePictureEditorContainer
          fieldDef={fieldDef}
          proportions={proportions}
          entity={data}
          fieldName={fieldName}
          onUpload={onUpload}
          onChange={onChangeImage}
        />
      )}
    </div>
  );
};

export default EditablePicture;
